import { get, cloneDeep } from 'lodash';
import { Dimensions } from './Dimensions';
import EncodingExecutor, { Frames } from './EncodingExecutor';
import { DataPrimitive } from './DataPrimitive';
import { OptionsStanza } from './AST';

type ScopeName = 'literal' | 'context' | 'datasources' | 'themes' | 'options';

export interface ScopedValue {
    location: ScopeName;
    val: DataPrimitive;
}

export class OptionScopes {
    executedOptions: Record<string, unknown>; // gets populated by DSL evaluation

    context: Record<string, unknown>; // also gets populated by DSL evaluation

    frames: Frames; // the data sources

    themeFunc: (themeVar) => any; // a function that must be provided, which resolves theme variables

    bypassCloneDeepOptionScope: boolean; // feature flag setting to bypass the call to cloneDeep inside the resolve function

    private local: Record<string, any>[] = []; // NOSONAR - mutated via push/pop

    private visitedPaths: Set<string> = new Set(); // NOSONAR - mutated via add/delete

    private static readonly SCOPES = ['local', 'context', 'datasources', 'options', 'themes'];

    constructor(
        optionsStanza: OptionsStanza,
        frames: Frames,
        themeFunc?: (themeVar) => any,
        bypassCloneDeepOptionScope?: boolean
    ) {
        this.context = optionsStanza.context;
        this.executedOptions = optionsStanza.options;
        this.frames = frames;
        this.themeFunc = themeFunc;
        this.bypassCloneDeepOptionScope = bypassCloneDeepOptionScope;
        this.local.push(this.executedOptions); // Stack. local starts off pointing to top level options
    }

    resolve(identifier: string): ScopedValue {
        const scopeParts = identifier.split('.');
        let scopedValue: ScopedValue = null;
        if (OptionScopes.isQualified(scopeParts)) {
            scopedValue = this.resolveQualifiedIdentifier(scopeParts);
        } else {
            scopedValue = this.resolveUnqualifiedIdentifier(scopeParts);
        }
        // bypassCloneDeepOptionScope ends here and toggles the use of `cloneDeep`.
        // The use of this function causes performance issues, esp with larger data sets
        // and was found not to be needed because every formatter that is used within the DSL
        // pipeline acts on a copy when need be. This saves on times where `> primary` is used
        // over and over
        if (this.bypassCloneDeepOptionScope) {
            return scopedValue;
        }
        return cloneDeep(scopedValue);
    }

    private static isQualified(scopeParts: string[]): boolean {
        const firstPart = scopeParts[0];
        return OptionScopes.SCOPES.includes(firstPart);
    }

    private resolveUnqualifiedIdentifier(scopeParts: string[]): any {
        // no qualifier has been given so we create qualified identifiers from all
        // that point to all possible scopes
        const qualifiers = OptionScopes.SCOPES;
        let scopedValue: ScopedValue;
        for (const qualifier of qualifiers) {
            const qualifiedIdentifier = [qualifier, ...scopeParts];
            scopedValue = this.resolveQualifiedIdentifier(qualifiedIdentifier);
            if (scopedValue.val) {
                break; // found it
            }
        }
        return scopedValue;
    }

    private resolveQualifiedIdentifier(scopeParts: string[]): ScopedValue {
        const qualifiedTargets = {
            context: this.context,
            options: this.executedOptions,
            datasources: this.frames,
            local: this.local[this.local.length - 1],
        };
        let val = null;
        const location = scopeParts[0] as ScopeName;
        if (location === 'themes' && this.themeFunc) {
            // themes must be looked up in themeRegistry
            const themeKey = scopeParts[1];
            val = this.themeFunc(themeKey);
        } else {
            val = get(qualifiedTargets, scopeParts);
            if (val) {
                val = new EncodingExecutor().eval(val, this, scopeParts);
            }
        }
        return { location, val };
    }

    public pushLocalScope(o: Record<string, unknown>) {
        this.local.push(o);
    }

    public popLocalScope() {
        this.local.pop();
    }

    addToVisitedList(path: string[]) {
        const pathStr = path.join('.');
        if (this.visitedPaths.has(pathStr)) {
            throw new Error(
                `Circular reference ${pathStr}, path history: ${new Array(...this.visitedPaths).toString()}`
            );
        }
        this.visitedPaths.add(pathStr);
    }

    removeFromVisitedList(path: string[]) {
        this.visitedPaths.delete(path.join('.'));
    }
}

export interface Scopes {
    context: Record<string, unknown>;
    vizDims: Dimensions;
    frames: Frames;
}
